import type { EmployeeGoldFactMonthRow } from '../types/employeeAnalysis'
import type { PeriodFilter } from './projectAnalysisPeriod'
import { isRecordInPeriod, MONTH_NAMES } from './projectAnalysisPeriod'

export interface ComputedEmployeeUserStats {
  userInfo: {
    full_name?: string
    username?: string
    email?: string
    is_active?: number | boolean
  }
  totalHours: number
  billableHours: number
  nonBillableHours: number
  utilizationRate: number
  totalActiveDays: number
  totalEntries: number
  lateEntries: number
  lateEntriesRate: number | null
  fullDayLeaves: number
  halfDayLeaves: number
  totalLeaveDays: number
  avgBillablePercent: number
  uniqueProjects: number
  projectsData: Array<{
    project_id: number
    project_name?: string
    total_hours: number
    billable_hours: number
    non_billable_hours: number
  }>
  hoursTrend: number | null
  monthlyData: EmployeeGoldFactMonthRow[]
}

const round2 = (v: number) => Math.round(Number(v) * 100) / 100

export function computeEmployeeUserStats(
  userData: EmployeeGoldFactMonthRow[] | null | undefined,
  userProfileFilter: PeriodFilter,
): ComputedEmployeeUserStats | null {
  if (!userData?.length) return null

  const filteredData = userData.filter((d) =>
    isRecordInPeriod(d.year, d.month, userProfileFilter),
  )
  if (filteredData.length === 0) return null

  const totalHours = filteredData.reduce(
    (sum, d) => sum + parseFloat(String(d.total_hours || 0)),
    0,
  )
  const billableHours = filteredData.reduce(
    (sum, d) => sum + parseFloat(String(d.billable_hours || 0)),
    0,
  )
  const nonBillableHours = filteredData.reduce(
    (sum, d) => sum + parseFloat(String(d.non_billable_hours || 0)),
    0,
  )
  const totalActiveDays = filteredData.reduce(
    (sum, d) => sum + parseInt(String(d.active_days || 0), 10),
    0,
  )
  const totalEntries = filteredData.reduce(
    (sum, d) => sum + parseInt(String(d.entries_count || 0), 10),
    0,
  )
  const lateEntries = filteredData.reduce(
    (sum, d) => sum + parseInt(String(d.late_entries_count || 0), 10),
    0,
  )
  const fullDayLeaves = filteredData.reduce(
    (sum, d) => sum + parseFloat(String(d.full_day_leaves || 0)),
    0,
  )
  const halfDayLeaves = filteredData.reduce(
    (sum, d) => sum + parseFloat(String(d.half_day_leaves || 0)),
    0,
  )
  const totalLeaveDays = filteredData.reduce(
    (sum, d) => sum + parseFloat(String(d.total_leave_days || 0)),
    0,
  )
  const avgBillablePercent =
    filteredData.reduce(
      (sum, d) => sum + parseFloat(String(d.billable_percentage || 0)),
      0,
    ) / filteredData.length

  const allProjects = new Map<
    number,
    {
      project_id: number
      project_name?: string
      total_hours: number
      billable_hours: number
      non_billable_hours: number
    }
  >()

  filteredData.forEach((month) => {
    month.projects_breakdown?.forEach((proj) => {
      const pId = proj.project_id
      const th = round2(parseFloat(String(proj.total_hours || 0)))
      const bh = round2(parseFloat(String(proj.billable_hours || 0)))
      const nb = round2(parseFloat(String(proj.non_billable_hours || 0)))
      if (allProjects.has(pId)) {
        const existing = allProjects.get(pId)!
        existing.total_hours = round2(existing.total_hours + th)
        existing.billable_hours = round2(existing.billable_hours + bh)
        existing.non_billable_hours = round2(existing.non_billable_hours + nb)
      } else {
        allProjects.set(pId, {
          ...proj,
          total_hours: th,
          billable_hours: bh,
          non_billable_hours: nb,
        })
      }
    })
  })

  const sortedByDate = [...filteredData].sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year
    return b.month - a.month
  })

  let hoursTrend: number | null = null
  if (sortedByDate.length >= 2) {
    const lastMonth = parseFloat(String(sortedByDate[0].total_hours || 0))
    const prevMonth = parseFloat(String(sortedByDate[1].total_hours || 0))
    if (prevMonth > 0) hoursTrend = ((lastMonth - prevMonth) / prevMonth) * 100
  }

  const first = userData[0]

  return {
    userInfo: {
      full_name: first.full_name,
      username: first.username,
      email: first.email,
      is_active: first.user_is_active,
    },
    totalHours,
    billableHours,
    nonBillableHours,
    utilizationRate: totalHours > 0 ? (billableHours / totalHours) * 100 : 0,
    totalActiveDays,
    totalEntries,
    lateEntries,
    lateEntriesRate: totalEntries > 0 ? (lateEntries / totalEntries) * 100 : null,
    fullDayLeaves,
    halfDayLeaves,
    totalLeaveDays,
    avgBillablePercent,
    uniqueProjects: allProjects.size,
    projectsData: Array.from(allProjects.values()).sort(
      (a, b) => b.total_hours - a.total_hours,
    ),
    hoursTrend,
    monthlyData: filteredData,
  }
}

export interface MonthlyChartPoint {
  name: string
  year: number
  month: number
  totalHours: number
  billableHours: number
  nonBillableHours: number
  utilizationRate: number
  lateSubmissionPct: number
  activeDays: number
  projects?: number
}

export function buildEmployeeMonthlyChartData(
  userStats: ComputedEmployeeUserStats | null,
): MonthlyChartPoint[] {
  if (!userStats) return []
  return userStats.monthlyData
    .map((d) => {
      const entries = parseInt(String(d.entries_count || 0), 10)
      const late = parseInt(String(d.late_entries_count || 0), 10)
      const latePct = entries > 0 ? Math.round((late / entries) * 1000) / 10 : 0
      return {
        name: `${d.year}-${String(d.month).padStart(2, '0')}`,
        year: d.year,
        month: d.month,
        totalHours: Math.round(parseFloat(String(d.total_hours || 0)) * 100) / 100,
        billableHours:
          Math.round(parseFloat(String(d.billable_hours || 0)) * 100) / 100,
        nonBillableHours:
          Math.round(parseFloat(String(d.non_billable_hours || 0)) * 100) / 100,
        utilizationRate: parseFloat(String(d.billable_percentage || 0)),
        lateSubmissionPct: latePct,
        activeDays: parseInt(String(d.active_days || 0), 10),
        projects: d.projects_worked,
      }
    })
    .sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year
      return a.month - b.month
    })
}

export function getMonthlyDetailsExportRows(
  monthlyData: EmployeeGoldFactMonthRow[],
  detailsYearFilter: string,
  detailsMonthFilter: string,
): Record<string, string | number>[] {
  if (!monthlyData.length) return []
  const filtered = monthlyData.filter((month) => {
    const yearMatch =
      detailsYearFilter === 'all' ||
      month.year === parseInt(detailsYearFilter, 10)
    const monthMatch =
      detailsMonthFilter === 'all' ||
      month.month === parseInt(detailsMonthFilter, 10)
    return yearMatch && monthMatch
  })
  const sorted = [...filtered].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year
    return b.month - a.month
  })
  return sorted.map((month) => {
    const util = parseFloat(String(month.billable_percentage || 0))
    return {
      Period: `${MONTH_NAMES[(month.month || 1) - 1]} ${month.year}`,
      'Total Hrs': parseFloat(String(month.total_hours || 0)),
      Billable: parseFloat(String(month.billable_hours || 0)),
      'Util. %': `${util.toFixed(0)}%`,
      'Active days': month.active_days != null ? month.active_days : '—',
      Projects: month.projects_worked ?? '—',
      'Full-day leave': parseFloat(String(month.full_day_leaves || 0)),
      'Half-day leave': parseFloat(String(month.half_day_leaves || 0)),
      'Total leave': parseFloat(String(month.total_leave_days || 0)),
      Late: parseInt(String(month.late_entries_count || 0), 10),
    }
  })
}
